<template>
  <div class="login-page">
    <!-- 动态背景装饰 -->
    <div class="bg-decoration">
      <div class="bg-orb bg-orb--1"></div>
      <div class="bg-orb bg-orb--2"></div>
      <div class="bg-orb bg-orb--3"></div>
      <div class="bg-grid"></div>
    </div>

    <div class="login-card">
      <!-- 品牌区 -->
      <div class="brand-section">
        <div class="brand-logo">
          <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" class="brand-icon">
            <circle cx="16" cy="16" r="14" fill="url(#login-heart)"/>
            <path d="M16 10C13.5 7.5 9.5 7.5 7.5 10C5.5 12.5 5.5 16 7.5 18L16 26L24.5 18C26.5 16 26.5 12.5 24.5 10C22.5 7.5 18.5 7.5 16 10Z" fill="white" fill-opacity="0.95"/>
            <defs>
              <linearGradient id="login-heart" x1="0" y1="0" x2="32" y2="32">
                <stop stop-color="#FF7E7E"/>
                <stop offset="1" stop-color="#FFA07A"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
        <h1 class="brand-title">时光同频</h1>
        <p class="brand-subtitle">时光不孤单，知己自同频</p>
      </div>

      <!-- 错误提示 -->
      <transition name="slide-down">
        <div v-if="error" class="error-tip">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ error }}
        </div>
      </transition>

      <!-- 登录表单 -->
      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-field">
          <div class="field-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <input
            type="text"
            v-model="form.username"
            placeholder="请输入用户名"
            :disabled="loading"
            class="field-input"
          >
        </div>

        <div class="form-field">
          <div class="field-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <input
            type="password"
            v-model="form.password"
            placeholder="请输入密码"
            :disabled="loading"
            class="field-input"
          >
        </div>

        <button type="submit" :disabled="loading" class="submit-btn">
          <span v-if="loading" class="btn-loading">
            <span class="spinner"></span> 登录中...
          </span>
          <span v-else>登录</span>
        </button>
      </form>

      <!-- 页脚 -->
      <div class="login-footer">
        <router-link to="/register" class="footer-link">还没有账号？<strong>立即注册</strong></router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
const store = useStore()
const router = useRouter()
const form = ref({ username: '', password: '' })
const loading = ref(false)
const error = ref('')

watch(() => store.state.error, (newVal) => {
  if (newVal) error.value = newVal
})

const handleLogin = async () => {
  if (!form.value.username.trim()) { error.value = '请输入用户名'; return }
  if (!form.value.password.trim()) { error.value = '请输入密码'; return }
  loading.value = true
  error.value = ''
  try {
    await store.dispatch('login', form.value)
    router.push('/')
  } catch (err) {
    console.error('登录失败')
  } finally { loading.value = false }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--app-bg-page);
  position: relative;
  overflow: hidden;
  padding: 20px;
}

/* ── 动态背景 ── */
.bg-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 126, 126, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 126, 126, 0.03) 1px, transparent 1px);
  background-size: 48px 48px;
}

.bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
}

.bg-orb--1 {
  width: 400px;
  height: 400px;
  background: rgba(255, 126, 126, 0.12);
  top: -120px;
  right: -80px;
  animation: orbFloat 12s ease-in-out infinite;
}

.bg-orb--2 {
  width: 320px;
  height: 320px;
  background: rgba(139, 124, 247, 0.10);
  bottom: -80px;
  left: -100px;
  animation: orbFloat 16s ease-in-out infinite reverse;
}

.bg-orb--3 {
  width: 200px;
  height: 200px;
  background: rgba(251, 191, 36, 0.08);
  bottom: 15%;
  right: 15%;
  animation: orbFloat 14s ease-in-out infinite 3s;
}

@keyframes orbFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(40px, -40px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

/* ── 登录卡片 ── */
.login-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  background: var(--app-bg-glass);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: var(--app-shadow-glass), 0 0 0 1px rgba(255, 126, 126, 0.06);
  padding: 44px 36px 32px;
}

/* ── 品牌区 ── */
.brand-section {
  text-align: center;
  margin-bottom: 32px;
}

.brand-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.brand-icon {
  width: 60px;
  height: 60px;
  filter: drop-shadow(0 4px 12px rgba(255, 126, 126, 0.3));
}

.brand-title {
  font-size: 1.625rem;
  font-weight: 800;
  background: linear-gradient(135deg, #FF7E7E, #FFA07A);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 6px;
  letter-spacing: 0.02em;
}

.brand-subtitle {
  font-size: 0.875rem;
  color: var(--app-text-secondary);
  font-weight: 400;
}

/* ── 错误提示 ── */
.slide-down-enter-active {
  transition: all 0.3s ease;
}
.slide-down-leave-active {
  transition: all 0.2s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.error-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 14px;
  margin-bottom: 18px;
  border-radius: 10px;
  background: #FEF2F2;
  color: #DC2626;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid #FECACA;
}

/* ── 表单 ── */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-field {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 52px;
  border-radius: 14px;
  border: 2px solid var(--app-border-light);
  background: var(--app-bg-soft);
  transition: all 0.25s ease;
}

.form-field:focus-within {
  border-color: var(--app-color-primary);
  background: #fff;
  box-shadow: 0 0 0 4px rgba(255, 126, 126, 0.08);
}

.field-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--app-text-muted);
  transition: color 0.25s ease;
}

.form-field:focus-within .field-icon {
  color: var(--app-color-primary);
}

.field-input {
  flex: 1;
  height: 100%;
  border: none;
  background: transparent;
  font-size: 0.9375rem;
  color: var(--app-text-primary);
  outline: none;
  font-family: inherit;
}

.field-input::placeholder {
  color: var(--app-text-muted);
}

.field-input:disabled {
  opacity: 0.6;
}

/* ── 提交按钮 ── */
.submit-btn {
  height: 52px;
  border: none;
  border-radius: 14px;
  background: var(--app-gradient-primary);
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  margin-top: 4px;
  letter-spacing: 0.02em;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(255, 126, 126, 0.35);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── 页脚 ── */
.login-footer {
  text-align: center;
  margin-top: 24px;
}

.footer-link {
  font-size: 0.875rem;
  color: var(--app-text-secondary);
  text-decoration: none;
  transition: color 0.2s;
}

.footer-link:hover {
  color: var(--app-color-primary);
}

.footer-link strong {
  font-weight: 600;
}

/* ── 响应式 ── */
@media (max-width: 480px) {
  .login-card {
    padding: 36px 24px 28px;
    border-radius: 20px;
  }

  .brand-icon {
    width: 52px;
    height: 52px;
  }

  .brand-title {
    font-size: 1.375rem;
  }

  .form-field {
    height: 48px;
    padding: 0 14px;
  }

  .submit-btn {
    height: 48px;
  }
}
</style>
